import React, { useState } from 'react';
import { Card, Modal, Table, Button, TablePaginationConfig, Space, Form, Input, Select } from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import styles from './provisioned-devices.module.scss';
import { DeviceType, ProvisionedDevice, ProvisionedDeviceExtended, StbGroupExtended } from '@services/types';
import api from '@services/api';
import { toast } from 'react-toastify';
import { getUserRoleByRoles, selectSearchFilter } from '@services/utils';
import { ReadProvisioningDevicesRequest, Role, StbGroupNameResponse, UserRequest } from '@openapi';
import { AppContext } from '@layouts/AppRouter';

const { Column } = Table;
const { Option } = Select;
const ProvisionedDevices: React.FC = () => {
  const { user } = React.useContext(AppContext);
  const [searchForm] = Form.useForm<ReadProvisioningDevicesRequest>();
  const [isLoading, setIsLoading] = useState(true);
  const [provDevices, setProvDevices] = useState<ProvisionedDeviceExtended[]>([]);
  const [provDevicesCount, setProvDevicesCount] = useState<any>([]);
  const [stbGroups, setStbGroups] = useState<Array<StbGroupNameResponse>>([]);
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const { deviceFilter, setDeviceFilter } = React.useContext(AppContext);
  const [pagination, setPagination] = React.useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    total: provDevices?.length,
  });

  React.useEffect(() => {
    Promise.all([
      api.administration.getDeviceTypes(),
      api.stbGroups.getStbGroupsFilter(),
      api.devices.getProvisionedDevices(),
    ])
      .then(([newDeviceTypes, newStbGroups, provisionedDevice]) => {
        setDeviceTypes(newDeviceTypes);
        setStbGroups(newStbGroups);
        if (deviceTypes) {
          setProvDevices(
            provisionedDevice.elements.map((provisioned: ProvisionedDeviceExtended) => ({
              ...provisioned,
              deviceTypeName: newDeviceTypes.find((newDeviceTypes) => newDeviceTypes.id === provisioned?.deviceTypeId)
                ?.deviceName,
              stbGroupName: newStbGroups.find((newStbGroups) => newStbGroups.id === provisioned?.stbGroupId)?.name,
            })),
          );
          setProvDevicesCount(provisionedDevice.totalElementCount);
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error('Devices was not loaded due to error');
      })
      .finally(() => setIsLoading(false));
  }, []);

  const showDeleteConfirm = (element: any) => {
    Modal.confirm({
      title: 'Are you sure delete this device?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api.devices.deleteProDevice(element?.macAddress);
          setProvDevices(provDevices.filter(({ macAddress }) => macAddress !== element?.macAddress));
          toast.success('Device was deleted successfully');
        } catch (error) {
          console.log('Device was not deleted ~ error', error);
          toast.error('Device was not deleted');
        }
      },
    });
  };

  const handleSearch = React.useCallback(
    async (newPagination = pagination) => {
      try {
        if (deviceFilter?.length) {
          setIsLoading(true);
          const values = await searchForm.validateFields();
          const newProDevice = await api.devices.getProvisionedDevices({
            ...values,
            filterDeviceTypeId: deviceFilter,
            limit: newPagination.pageSize,
            offset: (newPagination.current || 0) * (newPagination.pageSize || 0) - (newPagination.pageSize || 0),
          });
          setProvDevices(
            newProDevice.elements.map((provisioned: ProvisionedDeviceExtended) => ({
              ...provisioned,
              deviceTypeName: deviceTypes.find((deviceTypes) => deviceTypes.id === provisioned?.deviceTypeId)
                ?.deviceName,
              stbGroupName: stbGroups.find((stbGroups) => stbGroups.id === provisioned?.stbGroupId)?.name,
            })),
          );
          setPagination({
            current: newPagination.current,
            pageSize: newPagination.pageSize,
            showSizeChanger: true,
            total: newProDevice.totalElementCount || newProDevice.elements.length,
          });
          setDeviceFilter('');
        } else {
          setIsLoading(true);
          const values = await searchForm.validateFields();
          const newProDevice = await api.devices.getProvisionedDevices({
            ...values,
            limit: newPagination.pageSize,
            offset: (newPagination.current || 0) * (newPagination.pageSize || 0) - (newPagination.pageSize || 0),
          });
          setProvDevices(
            newProDevice.elements.map((provisioned: ProvisionedDeviceExtended) => ({
              ...provisioned,
              deviceTypeName: deviceTypes.find((deviceTypes) => deviceTypes.id === provisioned?.deviceTypeId)
                ?.deviceName,
              stbGroupName: stbGroups.find((stbGroups) => stbGroups.id === provisioned?.stbGroupId)?.name,
            })),
          );
          setPagination({
            current: newPagination.current,
            pageSize: newPagination.pageSize,
            showSizeChanger: true,
            total: newProDevice.totalElementCount || newProDevice.elements.length,
          });
        }
      } catch (err) {
        console.error('handleSearch ~ err', err);
        toast.error('Devices was not loaded due to error');
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchForm, deviceTypes, stbGroups],
  );

  const handleTableChange = React.useCallback(
    (newPagination: TablePaginationConfig) => {
      setPagination(newPagination);
      handleSearch(newPagination);
    },
    [handleSearch],
  );

  const onReset = React.useCallback(() => {
    searchForm.resetFields();
    handleSearch(pagination);
  }, [handleSearch, pagination, searchForm]);

  const proCount = (pagination.pageSize || 0) * (pagination.current || 0);
  const proItem = (proCount || 0) - ((pagination.pageSize || 0) - 1);

  const exportProvTable = async () => {
    try {
      const values = await searchForm.validateFields();
      const jobInd = await api.devices.exportDeviceProvisioned(values);
      toast.success('Provisioned devices export started successfully.');
    } catch (error) {
      toast.error('Provisioned devices isn`t exported');
    }
  };

  const role: UserRequest['role'] | any = getUserRoleByRoles(user?.roles || []);

  return (
    <>
      <Card title="Search" style={{ marginBottom: '1rem' }}>
        <Form className={styles.form} form={searchForm} name="searchForm" layout="vertical">
          <Form.Item name="filterSerialNumber" label="Serial Number" style={{ gridArea: 'serialNumber' }}>
            <Input />
          </Form.Item>
          <Form.Item name="filterMacAddress" label="Mac Address" style={{ gridArea: 'macAddress' }}>
            <Input />
          </Form.Item>
          <Form.Item name="filterStbGroupId" label="STB Group" style={{ gridArea: 'stbGroup' }}>
            <Select allowClear showSearch filterOption={selectSearchFilter}>
              {stbGroups.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="filterDeviceTypeId" label="Device Type" style={{ gridArea: 'deviceType' }}>
            <Select allowClear showSearch filterOption={selectSearchFilter}>
              {deviceTypes.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.deviceName}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            className={styles.filterButtons}
            style={{ gridArea: 'buttons' }}
            extra={
              <Space size={'middle'}>
                <Button type="primary" onClick={() => handleSearch(pagination)}>
                  Search
                </Button>
                <Button type={'default'} onClick={() => onReset()}>
                  Reset
                </Button>
              </Space>
            }
          ></Form.Item>
        </Form>
      </Card>
      <Card
        title={`Provisioned devices: ${proItem}-${proCount} / ${provDevicesCount}`}
        bodyStyle={{ padding: 0 }}
        extra={
          <Button type="default" onClick={() => exportProvTable()}>
            Export
          </Button>
        }
      >
        <Table
          dataSource={provDevices}
          pagination={pagination}
          loading={isLoading}
          onChange={handleTableChange}
          rowKey={(element: any) => element?.macAddress}
        >
          <Column
            title="MAC Address"
            dataIndex="macAddress"
            key="macAddress"
            render={(text, record: ProvisionedDeviceExtended) => record?.macAddress}
          />
          <Column
            title="Serial Number"
            dataIndex="deviceId"
            key="deviceId"
            render={(text, record: ProvisionedDeviceExtended) => record?.deviceId}
          />
          <Column
            align="center"
            title="Device Type"
            dataIndex="deviceTypeId"
            key="deviceTypeId"
            render={(text, record: ProvisionedDeviceExtended) => record?.deviceTypeName}
          />
          <Column
            align="center"
            title="Group"
            dataIndex="stbGroupName"
            key="stbGroupName"
            render={(text, record: ProvisionedDeviceExtended) => record.stbGroupName}
          />
          <Column
            title="Creation Date"
            dataIndex="provisionedAt"
            key="provisionedAt"
            align="center"
            render={(time: ProvisionedDevice['provisionedAt']) => time?.toLocaleDateString()}
          />
          {role && role !== Role.Quality && (
            <Column
              align="center"
              title="Action"
              key="action"
              render={(text, element: ProvisionedDevice['macAddress']) => (
                <DeleteOutlined onClick={() => showDeleteConfirm(element)} />
              )}
            />
          )}
        </Table>
      </Card>
    </>
  );
};
export default React.memo(ProvisionedDevices);
